import { API } from "aws-amplify";

export interface TokenData {
  token: string;
  expiresAt: string;
  expiresIn: number;
}

export interface TokenRefreshResult {
  success: boolean;
  token?: string;
  expiresAt?: string;
  error?: string;
}

class TokenManager {
  private token: string | null = null;
  private expiresAt: Date | null = null;
  private refreshPromise: Promise<TokenRefreshResult> | null = null;
  private refreshBuffer = 5 * 60 * 1000; // 5 minutes before expiry

  /**
   * Set the current token and expiration
   */
  setToken(tokenData: TokenData): void {
    this.token = tokenData.token;
    this.expiresAt = new Date(tokenData.expiresAt);
  }

  /**
   * Get the current token, refreshing if necessary
   */
  async getValidToken(): Promise<string | null> {
    if (!this.token || !this.expiresAt) {
      return null;
    }

    // Check if token needs refresh (5 minutes before expiry)
    const now = new Date();
    const timeUntilExpiry = this.expiresAt.getTime() - now.getTime();
    
    if (timeUntilExpiry <= this.refreshBuffer) {
      // Token is about to expire, refresh it
      const refreshResult = await this.refreshToken();
      if (refreshResult.success && refreshResult.token) {
        return refreshResult.token;
      } else {
        // Refresh failed, return current token (might still work)
        console.warn("Token refresh failed, using current token:", refreshResult.error);
        return this.token;
      }
    }

    return this.token;
  }

  /**
   * Check if current token is valid (not expired)
   */
  isTokenValid(): boolean {
    if (!this.token || !this.expiresAt) {
      return false;
    }
    return new Date() < this.expiresAt;
  }

  /**
   * Check if token needs refresh soon
   */
  needsRefresh(): boolean {
    if (!this.expiresAt) {
      return false;
    }
    const now = new Date();
    const timeUntilExpiry = this.expiresAt.getTime() - now.getTime();
    return timeUntilExpiry <= this.refreshBuffer;
  }

  /**
   * Refresh the current token
   */
  async refreshToken(): Promise<TokenRefreshResult> {
    // Prevent multiple simultaneous refresh requests
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    if (!this.token) {
      return { success: false, error: "No token to refresh" };
    }

    this.refreshPromise = this.performTokenRefresh();
    const result = await this.refreshPromise;
    this.refreshPromise = null;

    return result;
  }

  private async performTokenRefresh(): Promise<TokenRefreshResult> {
    try {
      const response = await API.post("llmpublicapi", "/customers/refreshToken", {
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.token && response.expiresAt) {
        this.setToken({
          token: response.token,
          expiresAt: response.expiresAt,
          expiresIn: response.expiresIn
        });

        return {
          success: true,
          token: response.token,
          expiresAt: response.expiresAt
        };
      } else {
        return { success: false, error: "Invalid refresh response" };
      }
    } catch (error: any) {
      console.error("Token refresh failed:", error);
      
      // If token is completely invalid, clear it
      if (error.response?.status === 401) {
        this.clearToken();
      }

      return { 
        success: false, 
        error: error.response?.data?.error || error.message || "Token refresh failed" 
      };
    }
  }

  /**
   * Clear the current token
   */
  clearToken(): void {
    this.token = null;
    this.expiresAt = null;
  }

  /**
   * Get time until token expires (in milliseconds)
   */
  getTimeUntilExpiry(): number | null {
    if (!this.expiresAt) {
      return null;
    }
    return this.expiresAt.getTime() - new Date().getTime();
  }

  /**
   * Get current token without refresh (for display purposes)
   */
  getCurrentToken(): string | null {
    return this.token;
  }

  /**
   * Get current expiration date
   */
  getCurrentExpiration(): Date | null {
    return this.expiresAt;
  }
}

// Export singleton instance
export const tokenManager = new TokenManager();

// Export class for testing
export { TokenManager };
